import React, { useCallback, useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import { erpAPI } from '../../services/api';
import Modal from '../../components/admin/Modal';
import Toast from '../../components/admin/Toast';
import { rows, errorText, money, variantDisplay, DataTable, Loading, Form } from '../../utils/erpHelpers';
import '../../components/admin/AdminFormStyles.css';
import './ERPManagement.css';

const InventoryStockSummary: React.FC = () => {
  const [data, setData] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const closeToast = useCallback(() => setMessage(null), []);
  const [form, setForm] = useState<Record<string, any>>({});
  const [locationFilter, setLocationFilter] = useState(() => sessionStorage.getItem('erp-location-filter') || '');
  const [locationType] = useState('');
  const [, setInventoryProducts] = useState<any[]>([]);
  const [variantEditModalOpen, setVariantEditModalOpen] = useState(false);
  const [editingVariantStock, setEditingVariantStock] = useState<any>(null);

  // Filter state
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [colorFilter, setColorFilter] = useState('');

  // Master options metadata state
  const [metaOptions, setMetaOptions] = useState<{
    brands: any[];
    categories: any[];
    sizes: any[];
    colors: any[];
  }>({ brands: [], categories: [], sizes: [], colors: [] });

  useEffect(() => {
    // Load metadata options once
    const loadMeta = async () => {
      try {
        const [brandRes, catRes, sizeRes, colorRes] = await Promise.all([
          erpAPI.list('brand-options'),
          erpAPI.list('categories'),
          erpAPI.list('size-options'),
          erpAPI.list('color-options'),
        ]);
        setMetaOptions({
          brands: rows(brandRes.data),
          categories: rows(catRes.data),
          sizes: rows(sizeRes.data),
          colors: rows(colorRes.data),
        });
      } catch (err) {
        console.error('Failed to load filter metadata:', err);
      }
    };
    loadMeta();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const paramsFor = (resource: string) => {
        const params: any = {};
        if (locationFilter) {
          if (resource === 'shop-prices') params.shop = locationFilter;
          else if (['inventory', 'products'].includes(resource)) params.location = locationFilter;
        }
        if (resource === 'inventory') {
          if (locationType) params.location_type = locationType;
          if (search) params.search = search;
          if (brandFilter) params.brand = brandFilter;
          if (categoryFilter) params.category = categoryFilter;
          if (sizeFilter) params.size = sizeFilter;
          if (colorFilter) params.color = colorFilter;
        }
        return Object.keys(params).length ? params : undefined;
      };
      const resources = ['locations', 'products', 'variants', 'inventory', 'shop-prices', 'website-discounts'];
      const responses = await Promise.all(resources.map(r => erpAPI.list(r, paramsFor(r))));
      const next: Record<string, any[]> = {};
      resources.forEach((name, i) => { next[name] = rows(responses[i].data); });
      setData(next);
      setInventoryProducts(next.products || []);
      const available = next.locations || [];
      if (!locationFilter && available.length === 1) setLocationFilter(String(available[0].id));
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
    finally { setLoading(false); }
  }, [locationFilter, locationType, search, brandFilter, categoryFilter, sizeFilter, colorFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const change = (event: Event) => {
      const location = String((event as CustomEvent).detail?.location || '');
      sessionStorage.setItem('erp-location-filter', location);
      setLocationFilter(location);
    };
    window.addEventListener('erp-location-change', change);
    return () => window.removeEventListener('erp-location-change', change);
  }, []);
  useEffect(() => {
    const fn = () => load(); window.addEventListener('admin-refresh', fn);
    return () => window.removeEventListener('admin-refresh', fn);
  }, [load]);

  const run = async (work: () => Promise<any>, success: string) => {
    try {
      await work();
      setMessage({ type: 'success', text: success });
      setForm({});
      await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const value = (key: string) => form[key] ?? '';

  const openVariantEditModal = (stock: any) => {
    setEditingVariantStock(stock);
    setForm({
      selling_price: stock.variant_selling_price || '',
      wholesale_price: stock.variant_wholesale_price || '',
      discount_code: stock.discount_codes?.[0]?.code || '',
    });
    setVariantEditModalOpen(true);
  };

  const showLocationColumn = !locationFilter;

  const modalField = (key: string, label: string, type = 'text', placeholder = '', hint = '') => (
    <div className="admin-form-row">
      <div className="admin-form-label">{label}</div>
      <div className="admin-form-input">
        <input type={type} value={value(key)} placeholder={placeholder} onChange={e => setForm({ ...form, [key]: e.target.value })} />
        {hint && <div className="admin-form-hint">{hint}</div>}
      </div>
    </div>
  );

  if (loading && !Object.keys(data).length) return <Loading />;

  return (
    <div className="erp-page">
      <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={closeToast} duration={message?.type === 'error' ? 5000 : 3500} />

      <div className="erp-workspace-toolbar">
        <div>
          <strong>Inventory Stock Summary</strong>
          <p>Balances are read-only here. Purchases, transfers, sales, returns, and reservations are the only stock-changing workflows.</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="erp-filterbar" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px', padding: '12px', background: 'var(--bg-card, #ffffff)', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)', alignItems: 'center' }}>
        <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
          <input
            type="text"
            className="admin-input"
            value={search}
            placeholder="Search Product / SKU..."
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
          />
        </div>
        {search && (
          <button
            type="button"
            className="erp-btn secondary"
            onClick={() => {
              setSearch('');
            }}
            style={{ padding: '8px 14px', borderRadius: '6px' }}
          >
            Reset Search
          </button>
        )}
      </div>

      <DataTable title="Current inventory" columns={[
        ...(showLocationColumn ? ['Location'] : []),
        'Product', 'Variant', 'Physical', 'Reserved', 'Available', 'Unit Cost', 'Pricing / Discount', ''
      ]} rows={(data.inventory || []).map(x => {
        const codes = (x.discount_codes || []).map((rule: any) => rule.code);
        const isWarehouse = x.location_type === 'WAREHOUSE';
        return [
          ...(showLocationColumn ? [x.location_name] : []),
          x.product_name,
          <strong>{variantDisplay(x)}</strong>,
          x.quantity,
          x.reserved_quantity,
          x.available_quantity,
          money(x.average_cost),
          <div style={{ lineHeight: 1.4 }}>
            {isWarehouse ? <><span>Selling: {money(x.variant_selling_price)}</span><br /><small>Wholesale: {money(x.variant_wholesale_price)}</small></> : <span>Shop Price: {money(x.variant_selling_price)}</span>}
            {codes.length > 0 && <><br /><small>Discount: {codes.join(', ')}</small></>}
          </div>,
          <button className="erp-icon" onClick={() => openVariantEditModal(x)} title={isWarehouse ? 'Edit pricing and discount' : 'Manage discount code'}>
            <Pencil size={15} />
          </button>
        ];
      })} />

      <Modal isOpen={variantEditModalOpen} onClose={() => setVariantEditModalOpen(false)} title="Edit variant prices" size="medium">
        <Form className="erp-modal-form" onSubmit={() => run(() => erpAPI.action('inventory', editingVariantStock?.id, 'pricing', {
          selling_price: editingVariantStock?.location_type === 'WAREHOUSE' ? +value('selling_price') : undefined,
          wholesale_price: editingVariantStock?.location_type === 'WAREHOUSE' ? +value('wholesale_price') : undefined,
          discount_code: value('discount_code'),
        }), 'Variant pricing updated.')}>
          <p className="erp-modal-intro">Only pricing is editable. Inventory quantity and unit cost remain controlled by stock movements.</p>
          <div className="admin-form-table">
            {editingVariantStock?.location_type === 'WAREHOUSE' && <>{modalField('wholesale_price', 'Wholesale price (৳)', 'number', '0.00', 'Warehouse transfer profit is wholesale price minus unit cost.')}{modalField('selling_price', 'Website selling price (৳)', 'number', '0.00')}</>}
            {modalField('discount_code', 'Discount code', 'text', 'Leave blank to remove', 'Add an active code, or clear this field to remove the current code from this location.')}
          </div>
          <div className="admin-form-actions">
            <button type="button" className="btn-cancel" onClick={() => setVariantEditModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-save">Save prices</button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default InventoryStockSummary;
